import createDVPointerModel from './HandTrackingControls.js'
import createDVTouchModel from './TouchTrackingControls.js'

export default function createXRControls(THREE, XRControllerModelFactory, XRHandModelFactory) {
  const DVPointerModel = createDVPointerModel(THREE)

  class XRControls extends THREE.Object3D {

    /**
     * Handles XR Input
     * @memberof module:THREE
     * @class XRControls
     * @param {*} scene The scene the XR controls are in
     * @param {Computer} desktop The desktop that the keyboard is connected to
     * @param {object} options 
     * @param {object} [options.hideControllers=false] Setting this true will hide controller models. Useful if a model is already implemented.
     * @param {boolean} [options.hideHands=false] Setting this true will hide hand models. Useful if a model is already implemented.
     * @param {number} [options.leftClickButton=0] The button index for left click. Default 0 (trigger button)
     * @param {number} [options.rightClickButton=5] The button index for right click. Default 5 (front y/b button)
     */

    constructor(
      controls,
      options = {},
    ) {
      super()
      this.controls = controls
      this.options = options

      this.scene = controls.scene
      this.renderer = controls.renderer
      this.clock = new THREE.Clock()
      this.delta = 0
      this.frameRate = 1 / 60


      this.leftClickButton = options.leftClickButton || 0
      this.rightClickButton = options.rightClickButton || 5

      this.create = this.create.bind(this)
      this.enterXR = this.enterXR.bind(this)
      this.exitXR = this.exitXR.bind(this)
      this.onXRFrame = this.onXRFrame.bind(this)
      this.fowardEvent = this.fowardEvent.bind(this)

      this.checkRayForIntersect = this.checkRayForIntersect.bind(this)
      this.session = this.renderer.xr.getSession()

      if (this.session) this.enterXR()

      this.renderer.xr.addEventListener('sessionstart', this.enterXR);
      this.renderer.xr.addEventListener('sessionend', this.exitXR);
      return this
    }

    enterXR(evt) {
      if (this.inSession) return
      this.inSession = true
      this.session = this.renderer.xr.getSession()
      this.onXRFrame()
      this.create()
    }

    exitXR(evt) {
      if (!this.inSession) return
      this.inSession = false
      this.session.cancelAnimationFrame(this.animationLoop)
      this.animationLoop = null
      this.session = null
      this.destroy()
    }

    create() {
      if (!this.renderer || !this.renderer.xr.getControllerGrip) {
        console.warn('no renderer controllers for xr controls')
        setTimeout(this.create, 500)
        return
      }

      this.initialParent = this.options.parent || this.scene
      this.initialParent.add(this)

      this.createControllers()
      this.createHands()
    }

    destroy() {
      try {
        this.clearCursors()
        const session = this.session
        if (session) session.cancelAnimationFrame(this.animationLoop)
        this.session = null
        this.clear()
        this.removeFromParent()
      } catch (error) {
        console.log(error)
      }
    }

    createControllers() {
      try {
        // to create controllers
        this.controllerOne = this.renderer.xr.getController(0)
        this.controllerTwo = this.renderer.xr.getController(1)

        this.controllerOne.focused = true
        this.controllerTwo.focused = false

        this.controllerOne.raycaster = new THREE.Raycaster()
        this.controllerTwo.raycaster = new THREE.Raycaster()

        const controllerModelFactory = new XRControllerModelFactory()

        const controllerOneGrip = this.renderer.xr.getControllerGrip(0)
        const controllerTwoGrip = this.renderer.xr.getControllerGrip(1)

        this.add(this.controllerOne)
        this.add(this.controllerTwo)
        if (!this.options.hideControllers) {
          //add models
          controllerOneGrip.add(controllerModelFactory.createControllerModel(controllerOneGrip))
          controllerTwoGrip.add(controllerModelFactory.createControllerModel(controllerTwoGrip))
        }

        this.controllerOne.grip = controllerOneGrip
        this.controllerTwo.grip = controllerTwoGrip

        this.add(controllerOneGrip)
        this.add(controllerTwoGrip)

        this.dispatchEvent({type: 'loadedControllers'})
      } catch (error) {
        console.log(error)
      }
    }

    createHands() {
      try {
        if (this.handOnePointer) this.handOnePointer.clear()
        if (this.handTwoPointer) this.handOnePointer.clear()
        // to create hands
        const handModelFactory = new XRHandModelFactory()
        this.handOne = this.renderer.xr.getHand(0)
        this.handTwo = this.renderer.xr.getHand(1)


        const handOnePointer = new DVPointerModel(
          this.handOne,
          this.controllerOne,
        )
        const handTwoPointer = new DVPointerModel(
          this.handTwo,
          this.controllerTwo,
        )

        this.handOne.add(handOnePointer)
        this.handTwo.add(handTwoPointer)


        if (!this.options.hideHands) {
          //add models
          this.handOne.add(handModelFactory.createHandModel(this.handOne, 'mesh'))
          this.handTwo.add(handModelFactory.createHandModel(this.handTwo, 'mesh'))
        }

        this.controllerOne.grip = handOnePointer
        this.controllerTwo.grip = handTwoPointer

        this.add(this.handTwo)
        this.add(this.handOne)

        this.handOnePointer = handOnePointer
        this.handTwoPointer = handTwoPointer
      } catch (error) {
        console.log(error)
      }
    }


    onXRFrame(time, frame) {
      this.delta += this.clock.getDelta();
      const session = this.session
      if (!session) {
        this.session = this.renderer.xr.getSession()
        setTimeout(this.onXRFrame, 500)
        return
      }

      if (this.delta > this.frameRate) {
        this.delta = this.delta % this.frameRate;
        try {
          const inputSources = session.inputSources
          for (let source of inputSources) {
            this.processSource(source, frame)
          }

          this.updateRays(this.controllerOne)
          this.updateRays(this.controllerTwo)

          this.drawCursor(this.controllerOne)
          this.drawCursor(this.controllerTwo)

        } catch (e) {
          console.log(e)
        }
      }
      this.animationLoop = session.requestAnimationFrame(this.onXRFrame)
    }

    processSource(source, frame) {
      if (!source) {
        console.warn('missing source')
        return
      }
      if (!frame) {
        console.warn('missing frame')
        return
      }
      const xrControllers = this.processController(source, frame)
      if (xrControllers) {
        const { xrController, notSelectedXrController } = xrControllers
        this.processJoystick(source.gamepad)
        this.processControllerButtons(source, xrController, notSelectedXrController)
      }
    }

    processController(controller, frame) {
      let xrController = this.controllerOne
      let notSelectedXrController = this.controllerTwo
      try {
        const xrRefSpace = this.renderer.xr.getReferenceSpace()
        const targetRayPose = frame.getPose(controller.targetRaySpace, xrRefSpace);
        if (!targetRayPose) return

        const origin = new THREE.Vector3(targetRayPose.transform.position.x, targetRayPose.transform.position.y, targetRayPose.transform.position.z)
        const controllerOneDistance = this.controllerOne.position.distanceTo(origin)
        const controllerTwoDistance = this.controllerTwo.position.distanceTo(origin)
        if (controllerOneDistance > controllerTwoDistance) {
          xrController = this.controllerTwo
          notSelectedXrController = this.controllerOne
        }

        xrController.handedness = controller.handedness
      } catch (e) {
        console.log(e)
      }
      return { xrController, notSelectedXrController }
    }

    processControllerButtons(controller, xrController, notSelectedXrController) {
      try {
        const buttons = controller.gamepad.buttons
        let selectButton = buttons[0]

        if (buttons.length === 1) {
          //only one button
        } else if (buttons.length <= this.leftClickButton + 1) {
          console.warn('select button not found')
          return
        } else {
          selectButton = buttons[this.leftClickButton]
        }

        if (!controller.wasPressed && selectButton.pressed) {
          //select start
          controller.wasPressed = true
          xrController.focused = true
          notSelectedXrController.focused = false
          this.onSelectStart(xrController)
        } else if (controller.wasPressed && !selectButton.pressed) {
          //select end
          controller.wasPressed = false
          this.onSelectEnd(xrController)
        }

        if (buttons.length <= this.rightClickButton + 1) return
        const rightClickButton = buttons[this.rightClickButton]

        if (!controller.wasRightClicked && rightClickButton.pressed) {
          //select start
          controller.wasRightClicked = true
          xrController.focused = true
          notSelectedXrController.focused = false
          this.onRightSelectStart(xrController)
        } else if (controller.wasRightClicked && !selectButton.pressed) {
          //select end
          controller.wasRightClicked = false
        }
      } catch (e) {
        console.log(e)
      }
    }


    processJoystick(gamepad) {
      let shouldPost, x, y
      if (gamepad.axes.length < 0) return
      for (let axis of gamepad.axes) {
        if (axis !== 0) shouldPost = true
      }
      if (gamepad.axes[0] !== 0 || gamepad.axes[1] !== 0) {
        x = gamepad.axes[0]
        y = gamepad.axes[1]
      } else {
        x = gamepad.axes[2]
        y = gamepad.axes[3]
      }
      if (y < 0) y + y - 1
      x = 5 * x * 1
      y = 20 * y * 1
      if (shouldPost) {
        this.dispatchEvent({ type: 'scroll', data: { deltaX: x, deltaY: y } })
      }
    }

    onSelectStart(controller) {
      const raycaster = controller.raycaster
      this.dispatchEvent({ type: 'selectStart', data: { raycaster, type: 'xr', controller } });
    }

    onSelectEnd(controller) {
      this.dispatchEvent({ type: 'selectEnd', data: { raycaster: controller.raycaster, type: 'xr', controller } });
    }

    onRightSelectStart(controller) {
      this.dispatchEvent({ type: 'rightClick', data: { raycaster: controller.raycaster } });
    }

    updateRays(controller) {
      const cPosition = controller.position
      const cInactive = cPosition.x === 0 && cPosition.y === 0 && cPosition.z === 0
      if (cInactive) return

      const controllerOrigin = controller.getWorldPosition(new THREE.Vector3())
      const controllerDirection = controller.getWorldDirection(new THREE.Vector3()).negate()
      controller.raycaster.set(controllerOrigin, controllerDirection)

      if (this.handOne.visible) {
        this.controllerType = 'hand'
      } else {
        this.controllerType = 'controller'
      }

      if (controller.focused)
        this.dispatchEvent({ type: 'rayMove', data: { raycaster: controller.raycaster, controller } })
    }

    checkRayForIntersect(raycaster) {
      try {
        if (!raycaster) return
        const intersectableObjects = [...this.controls.intersectableObjects]
        const cleanedObjects = intersectableObjects.filter(object => {
          return object && object.uuid
        })
        let intersects = raycaster.intersectObjects(cleanedObjects, true)
        if (intersects.length > 0) {
          const intersect = intersects[0]
          this.intersect = intersect
          return intersect
        } else {
          return null
        }
      } catch (e) {
        return null
      }
    }

    clearCursors() {
      this.scene.remove(this.controllerOne.rayLine)
      this.scene.remove(this.controllerTwo.rayLine)
      this.controllerOne.rayLine = null
      this.controllerTwo.rayLine = null

      this.scene.remove(this.controllerOne.cursor)
      this.scene.remove(this.controllerTwo.cursor)
      this.controllerOne.cursor = null
      this.controllerTwo.cursor = null
    }

    drawCursor(controller) {
      const raycaster = controller.raycaster
      if (raycaster) {
        const intersect = this.checkRayForIntersect(raycaster)
        let point = new THREE.Vector3()
        let cursorPoint = new THREE.Vector3()
        if (intersect) {
          point = intersect.point
          cursorPoint = raycaster.ray.at(intersect.distance, intersect.point)
        } else {
          raycaster.ray.at(5, point)
        }

        const points = [raycaster.ray.origin, point]

        let rayLine = controller.rayLine, cursor = controller.cursor

        if (!controller.rayLine) {
          rayLine = this.cylinderMesh(points[0], points[1])
          controller.rayLine = rayLine
          this.scene.add(rayLine)
        }

        if (!controller.cursor) {
          cursor = this.cursorMesh(0.0075)
          controller.cursor = cursor
          this.scene.add(cursor)
        }

        rayLine.visible = this.controllerType === 'controller'

        this.updateCylinderMesh(controller.rayLine, points[0], points[1])

        cursor.position.set(cursorPoint.x, cursorPoint.y, cursorPoint.z)
        this.controls.raycaster = raycaster
      }
    }

    cursorMesh(radius) {
      const cursorGeometry = new THREE.SphereGeometry(radius, 10, 10)
      const cursorMaterial = new THREE.MeshBasicMaterial()
      cursorMaterial.transparent = true
      cursorMaterial.opacity = 0.5
      cursorMaterial.reflectivity = 0
      // cursorMaterial.depthWrite = false
      // cursorMaterial.depthTest = false

      const cursor = new THREE.Mesh(cursorGeometry, cursorMaterial);  // and do the trick with orienation
      cursor.renderOrder = 100
      return cursor
    }

    cylinderMesh(vstart, vend) {

      var cylGeom = new THREE.CylinderGeometry(0.001, 0.003, 1, 16);
      cylGeom.translate(0, 0.5, 0);
      cylGeom.rotateX(Math.PI / 2);

      const material = new THREE.MeshBasicMaterial({
        color: 'white',
        opacity: 0.5,
        transparent: true,
        reflectivity: 0
      });

      // material.depthWrite = false
      // material.depthTest = false

      const cylinder = new THREE.Mesh(cylGeom, material);


      cylinder.position.copy(vstart);
      cylinder.lookAt(vend);  // and do the trick with orienation

      return cylinder
    }

    updateCylinderMesh(cylinder, vstart, vend) {
      var cylLength = new THREE.Vector3().subVectors(vend, vstart).length();
      cylinder.scale.z = cylLength
      cylinder.position.copy(vstart);
      cylinder.lookAt(vend);  // and do the trick with orienation
    }

    fowardEvent(event) {
      this.dispatchEvent(event)
    }
  }

  return XRControls
}